import { css } from '@emotion/react';
import { SocialProvider } from '@inflearn/app-shell';
import { ActionIcon, Box, Icon } from '@inflearn/ds-react';
import { faApple } from '@inflearn/free-brands-svg-icons/faApple';
import { faGithub } from '@inflearn/free-brands-svg-icons/faGithub';
import { faComment } from '@inflearn/pro-solid-svg-icons/faComment';
import type { ReactNode } from 'react';

const socialProviderNameMap = {
  [SocialProvider.Kakao]: '카카오',
  [SocialProvider.Google]: '구글',
  [SocialProvider.Apple]: '애플',
  [SocialProvider.Github]: '깃허브',
} as const;

const icons: Record<
  SocialProvider,
  {
    color: string;
    icon: ReactNode;
  }
> = {
  [SocialProvider.Kakao]: {
    color: 'yellow.4',
    icon: <Icon color="dark.5" icon={faComment} />,
  },
  [SocialProvider.Google]: {
    color: 'gray.1',
    icon: <GoogleLogo />,
  },
  [SocialProvider.Apple]: {
    color: 'white',
    icon: <Icon color="dark" icon={faApple} />,
  },
  [SocialProvider.Github]: {
    color: 'white',
    icon: <Icon color="dark" icon={faGithub} />,
  },
} as const;

export function SocialLoginButtonForWebview({
  provider,
  onClick,
}: {
  provider: SocialProvider;
  onClick: () => void;
}) {
  const providerIcon = icons[provider];

  return (
    <Box css={css({ position: 'relative' })}>
      <ActionIcon
        size={48}
        variant="filled"
        color={providerIcon.color}
        onClick={onClick}
        sx={(theme) => ({
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
          ...(providerIcon.color === 'white' && {
            backgroundColor: providerIcon.color,
            '&:hover': {
              backgroundColor: theme.colors.gray[1],
            },
          }),
        })}
        aria-label={`${socialProviderNameMap[provider]}로 로그인`}
      >
        {providerIcon.icon}
      </ActionIcon>
    </Box>
  );
}

export function GoogleLogo({ size, fill }: SvgIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size || 18}
      height={size || 18}
      fill={fill}
      viewBox="0 0 18 18"
    >
      <path
        fill="#4285F4"
        d="M17.785 9.169c0-.738-.06-1.276-.189-1.834h-8.42v3.328h4.942c-.1.828-.638 2.073-1.834 2.91l-.016.112 2.662 2.063.185.018c1.694-1.565 2.67-3.867 2.67-6.597z"
      ></path>
      <path
        fill="#34A853"
        d="M9.175 17.938c2.422 0 4.455-.797 5.94-2.172l-2.83-2.193c-.758.528-1.774.897-3.11.897-2.372 0-4.385-1.564-5.102-3.727l-.105.01-2.769 2.142-.036.1c1.475 2.93 4.504 4.943 8.012 4.943z"
      ></path>
      <path
        fill="#FBBC05"
        d="M4.073 10.743c-.19-.558-.3-1.156-.3-1.774 0-.618.11-1.216.29-1.774l-.005-.119L1.254 4.9l-.091.044C.555 6.159.206 7.524.206 8.969c0 1.445.349 2.81.957 4.026l2.91-2.252z"
      ></path>
      <path
        fill="#EB4335"
        d="M9.175 3.468c1.684 0 2.82.728 3.468 1.335l2.531-2.471C13.62.887 11.598 0 9.175 0 5.667 0 2.638 2.013 1.163 4.943l2.9 2.252c.727-2.162 2.74-3.727 5.112-3.727z"
      ></path>
    </svg>
  );
}

export type SvgIconProps = {
  size?: number;
  fill?: string;
};
